import { useCallback, useEffect, useState } from 'react';

import { Box, Flex, Text } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import ArrowIcon from 'images/icons/arrow-right.svg';

import { Button } from '@/components/atoms';

type StickyCtaProps = {
  title: string;
  subttitle: string;
  buttonCaption: string;
  buttonLink: string;
  sectionId: string;
};
const MotionFlex = motion(Flex);

export const StickyCta = ({ title, subttitle, buttonCaption, buttonLink, sectionId }: StickyCtaProps) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleScroll = useCallback(() => {
    const section = document.getElementById(sectionId);
    if (!section) return;

    const sectionRect = section.getBoundingClientRect();
    const sectionTop = sectionRect.top + window.scrollY;
    const scrollPosition = window.scrollY + window.innerHeight;

    if (scrollPosition < sectionTop) {
      setIsVisible(true);
    } else if (window.scrollY > sectionTop + section.offsetHeight) {
      setIsVisible(false);
    } else {
      setIsVisible(false);
    }
  }, [sectionId]);

  useEffect(() => {
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  if (!isVisible) return null;
  return (
    <AnimatePresence>
      {isVisible && (
        <MotionFlex
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 30 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          position="fixed"
          p={6}
          boxShadow="lg"
          bottom={4}
          left={4}
          bg="white"
          flexDir="column"
          gap={2}
          zIndex="overlay"
        >
          <Flex flexDir="column" gap={2} ml={3} maxW="300px">
            <Text
              variant={{
                base: 'text-md/lineHeight-6/font-semibold',
              }}
              color="purple.500"
            >
              {title}
            </Text>
            <Text
              color="purple.400"
              variant={{
                base: 'text-md/lineHeight-5/font-normal',
              }}
              alignItems="center"
            >
              {subttitle}
              <Box as="span" role="img" aria-label="downward pointing hand" ml={1}>
                👇
              </Box>
            </Text>
          </Flex>
          <Button
            variant="light"
            label={buttonCaption}
            rightIcon={<ArrowIcon width="12px" />}
            mt={{ base: 5 }}
            url={buttonLink}
            width="300px"
            size="md"
          />
        </MotionFlex>
      )}
    </AnimatePresence>
  );
};
